// Archive record component displays the modal content.
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Checkbox, message } from 'antd';

import ModalWrapper from './ModalWrapper';
import { captureAnalytics } from '../../actions/preload';
import { validSendEmail as validEmail } from '../../global/validation';
import {
  hasFeatureInLocalStorage,
  INBOX_BACKGROUND_REFRESH_FEATURE,
} from '../../global/featureFlags';

const errorStyle = {
  color: 'red',
};

class ArchiveRecord extends Component {
  constructor(props, context) {
    super(props, context);

    // map modal data to local state
    this.state = {
      error: false,
      archiveRecordsResultMessage: '',
      // used to track when the request has been made and to disable the archive button in modal so users don't click twice
      completedArchiveRecord: false,
      // set the moment Archive is clicked; locks the checkbox, whose value is read then
      archiving: false,
      // "Don't ask me again": saved to users.confirm_before_archiving when the user
      // confirms the archive, never on Cancel.
      dontAskAgain: false,
    };
  }

  componentDidMount() {
    const { modalType } = this.props;
    if (modalType.autoArchive) {
      this.archive();
    }
  }

  archive = async () => {
    const {
      modalType,
      archiveRecords,
      archiveReceivedRecords,
      hideModal,
    } = this.props;
    const { dontAskAgain } = this.state;
    this.setState({ archiving: true });

    // Saved alongside the archive rather than before it, so the opt-out adds no wait.
    const savingPreference = dontAskAgain ? this.saveDontAskAgain() : Promise.resolve();

    if (modalType.inboxLink === '/app/uploads') {
      await archiveRecords(modalType.data.tokens);
    } else {
      await archiveReceivedRecords(modalType.data.tokens);
    }
    // Settle before the reload below can cut the request off.
    await savingPreference;

    const { archiveErrorMessage } = this.props;
    this.setState({ completedArchiveRecord: true });
    let message = 'Successfully archived all record(s)';
    if (archiveErrorMessage) {
      this.setState({ error: true });
      message = 'We were unable to archive the selected records. Please try again';
    }

    this.setState({ archiveRecordsResultMessage: message });
    // With inbox_background_refresh on, the archive action requests a background
    // refresh of the inbox table (REQUEST_INBOX_TABLE_REFRESH), so no page reload
    // is needed — the rows update behind the modal while the result message is
    // showing. Off, keep the legacy full page reload.
    // reloadOnSuccess callers (the inline inbox viewer overlay) need the reload
    // regardless of the flag, but only on SUCCESS: the overlay is local component
    // state the background refresh can't clear (it's deferred while the viewer is
    // open), so without the reload it would stay open on the just-archived record.
    // On failure the record wasn't archived — leave the viewer open (don't yank the
    // user to the inbox). The flag-driven path (bulk callers) is unchanged.
    setTimeout(() => {
      hideModal();
      const shouldReload = modalType.reloadOnSuccess
        ? !archiveErrorMessage
        : !hasFeatureInLocalStorage(INBOX_BACKGROUND_REFRESH_FEATURE);
      if (shouldReload) {
        window.location.reload();
      }
    }, 2500);
  };

  // A failed save must not block the archive the user just confirmed; they will be
  // asked again next time and can still turn the prompt off in Settings.
  saveDontAskAgain = async () => {
    const { updateUserDetails, modalType } = this.props;
    try {
      await updateUserDetails({ confirm_before_archiving: false }, { rejectOnError: true });
      captureAnalytics('record_archive_confirm_disabled', { source: 'archive_modal', inbox: modalType.inboxLink });
    } catch {
      message.warning({ content: 'We couldn\'t save your preference. You can turn off the archive confirmation in Settings.' });
    }
  };

  render() {
    const customContentStyle = {
      width: '45%',
    };

    const errorModalStyle = {
      width: '20%',
    };

    const {
      hideModal, archiveErrorMessage, isLoading, modalType,
    } = this.props;
    const {
      error, archiveRecordsResultMessage, completedArchiveRecord, dontAskAgain, archiving,
    } = this.state;

    if (error) {
      return (
        <ModalWrapper
          hideModal={hideModal}
          customContentStyle={errorModalStyle}
          action={hideModal}
          actionName="OK"
          modalTitle="Archive Record"
        >
          <p style={errorStyle}>{ archiveErrorMessage || 'An error has occurred. Please try again' }</p>
        </ModalWrapper>
      );
    }

    return (
      <ModalWrapper
        hideModal={hideModal}
        customContentStyle={customContentStyle}
        action={this.archive}
        actionName="Archive"
        dismiss="Cancel"
        modalTitle="Archive Records"
        isLoading={isLoading || completedArchiveRecord}
      >
        <span>
          {!modalType.autoArchive && (
            <>
              <span style={{ fontWeight: '600' }}>
                Are you sure you want to archive these records?
              </span>
              <br />
              <br />
            </>
          )}
          Recipients of this record will still have access to this record. After
          the record is archived,
          {' '}
          {hasFeatureInLocalStorage(INBOX_BACKGROUND_REFRESH_FEATURE)
            ? 'the list will update automatically.'
            : 'the page will automatically refresh.'}
          <br />
          <br />
          {modalType.offerDontAskAgain && !modalType.autoArchive && (
            <>
              <Checkbox
                checked={dontAskAgain}
                disabled={archiving || completedArchiveRecord}
                onChange={(e) => this.setState({ dontAskAgain: e.target.checked })}
              >
                Don&apos;t ask me again
              </Checkbox>
              <div style={{ color: 'rgba(0, 0, 0, 0.45)', fontSize: '12px', marginTop: '4px' }}>
                You can turn this back on in Settings.
              </div>
              <br />
            </>
          )}
          {archiveRecordsResultMessage && (
            <p
              style={{
                textAlign: 'center',
                archiveBottom: '0px',
                fontStyle: 'italic',
              }}
            >
              {archiveRecordsResultMessage}
            </p>
          )}
        </span>
      </ModalWrapper>
    );
  }
}

ArchiveRecord.contextTypes = {
  router: PropTypes.object.isRequired,
};

ArchiveRecord.defaultProps = {
  archiveErrorMessage: '',
  updateUserDetails: async () => {},
};

ArchiveRecord.propTypes = {
  hideModal: PropTypes.func.isRequired,
  modalType: PropTypes.object.isRequired,
  archiveRecords: PropTypes.func.isRequired,
  archiveReceivedRecords: PropTypes.func.isRequired,
  archiveErrorMessage: PropTypes.string,
  updateUserDetails: PropTypes.func,
};

export default ArchiveRecord;
